import classNames from "classnames";
import { useContext, useState } from "react";
import Icon from "~/assets/icons/Icon";
import { IconType } from "~/assets/icons/iconPaths";
import Button from "../Button";

import "./index.scss";
import BuyButton from "../BuyButton";
import { CartContext } from "~/context/cart";
import { showToast } from "~/utils/toast";

const initialQuantity = 1;
const animationDuration = 400;

function QuantitySelector({
  productId,
  className,
  isUnique,
}: {
  productId: number;
  className?: string;
  isUnique?: boolean;
}) {
  const { addToCart, checkIfInCart } = useContext(CartContext);

  const [addingToBasket, setAddingToBasket] = useState(false);
  const [quantity, setQuantity] = useState(initialQuantity);

  const handleBuyButtonClick = async () => {
    if (isUnique && alreadyInCart) {
      return showToast(
        "info",
        "Unikavare",
        "Du kan kun tilføje én unikavare til kurven"
      );
    }
    setAddingToBasket(true);

    await addToCart(productId, quantity);
    setQuantity(initialQuantity);

    setTimeout(() => {
      setAddingToBasket(false);
    }, animationDuration);
  };

  const onQuantityChange = (type: "increase" | "decrease") => {
    if (isUnique) {
      return showToast(
        "info",
        "Unikavare",
        "Du kan kun tilføje én unikavare til kurven"
      );
    }

    if (type === "increase") {
      setQuantity((prev) => prev + 1);
    } else {
      setQuantity((prev) => prev - 1 || 1);
    }
  };

  const alreadyInCart = checkIfInCart(productId);

  return (
    <div
      className={classNames(
        addingToBasket
          ? "QuantitySelector QuantitySelector--animate"
          : "QuantitySelector",
        "flex items-center justify-center gap-2 p-3 rounded-xl max-w-[22rem]",
        className
      )}
      style={{
        animationDuration: `${animationDuration}ms`,
      }}
    >
      <div
        className="grid grid-cols-3 items-center justify-items-center"
        aria-label="Antal produkter der skal lægges i kurven"
      >
        <QuantitySelectorButton
          onClick={() => onQuantityChange("decrease")}
          icon="minus"
          aria-label="Fjern én"
        />
        <span aria-live="assertive">{quantity}</span>
        <QuantitySelectorButton
          onClick={() => onQuantityChange("increase")}
          icon="plus"
          aria-label="Tilføj én"
        />
      </div>
      <BuyButton
        className="relative whitespace-nowrap grow"
        onClick={handleBuyButtonClick}
        loading={addingToBasket}
        variant={isUnique && alreadyInCart ? "disabled" : "primary"}
      >
        {isUnique && alreadyInCart ? "Allerede i kurv" : "Tilføj til kurv"}
      </BuyButton>
    </div>
  );
}

interface QuantitySelectorButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: IconType;
}

export function QuantitySelectorButton({
  icon,
  ...rest
}: QuantitySelectorButtonProps) {
  return (
    <Button
      className="w-12 h-12 flex justify-center items-center rounded-md hover:border hover:border-neutral-300 transition-colors !shadow-none"
      {...rest}
    >
      <Icon name={icon} />
    </Button>
  );
}

export default QuantitySelector;
